import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { isAlive } from 'webapp/utils/common';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Source from 'webapp/models-custom/source';
import type SourceType from 'webapp/models-custom/source-type';

import type { AfterConnectFn } from 'webapp/services/connect';

interface Args {
	sourceType: SourceType | null;
	onConnectSource: (sourceKey: string, afterConnect: AfterConnectFn) => void;
	onCreateNewDataboard: (source: Source | null) => void;
	onViewMetricLibrary?: (source: Source | null) => void;
}

export default class WizardSourceMetricStepsComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	get isTokenWrapper() {
		return this.args.sourceType?.isTokenWrapper;
	}

	get isCustom() {
		return this.args.sourceType?.key === 'Custom';
	}

	get isDbWithQB() {
		return this.args.sourceType?.isDb;
	}

	get isEmptyVisualizationFlow() {
		// this includes sources that have metrics but simply dont have templates / datablocks prepped yet
		return !this.isTokenWrapper && !this.isCustom && !this.isDbWithQB;
	}

	get readMoreUrl() {
		return this.args.sourceType?.genericGuideLink;
	}

	get showReadMore() {
		return this.readMoreUrl && !this.accountService.hideHelpArticles;
	}

	get connectStepNumber() {
		return this.showReadMore ? 2 : 1;
	}

	get sourceTypeName() {
		return this.args.sourceType?.name;
	}

	get connectStepText() {
		if (this.isTokenWrapper) {
			return `Create a pushable ${this.sourceTypeName} token in ${this.companyName}`;
		} else if (this.isCustom) {
			return `Create a pushable token in ${this.companyName}`;
		} else {
			return `Connect ${this.sourceTypeName} with ${this.companyName}`;
		}
	}

	get connectStepButtonText() {
		if (this.isTokenWrapper || this.isCustom) {
			return 'New token <ui-icon name="External" size="sm"></ui-icon>';
		} else {
			return '<ui-icon name="Connect" size="sm"></ui-icon> Connect';
		}
	}

	get designerStepNumber() {
		let step = this.connectStepNumber;
		if (this.isTokenWrapper || this.isCustom) {
			step++;
		}
		return step;
	}

	get connectStepIcon() {
		return this.getStepIcon(this.connectStepNumber);
	}

	get designerStepIcon() {
		return this.getStepIcon(this.designerStepNumber);
	}

	getStepIcon(num: number): IconName {
		const icons: Record<number, IconName> = {
			1: 'NumberCircle1',
			2: 'NumberCircle2',
			3: 'NumberCircle3',
			4: 'NumberCircle4',
			5: 'NumberCircle5',
		};
		return icons[num] || 'NumberCircle1';
	}

	// ----------- Custom token flow: buttons enabling ---------

	@tracked wasEditMetrickeyPopupOpened = false;
	@tracked connectedSource: Source | null = null;

	get isCustomCreateMetricBtnDisabled() {
		const sourceType = this.connectedSource?.sourceType;

		return !sourceType?.isWithCustomToken || sourceType?.isTokenWrapper;
	}

	get isViewMetricLibraryBtnDisabled() {
		return this.isCustomCreateMetricBtnDisabled || !this.wasEditMetrickeyPopupOpened;
	}

	get disableCustomMetricButton() {
		return !this.connectedSource;
	}

	get companyName() {
		return this.accountService.shownCompanyName;
	}

	@action
	connectSource() {
		const sourceType = this.args.sourceType;
		if (!sourceType) {
			return;
		}

		this.args.onConnectSource(sourceType?.key, (isSuccess, source) => {
			if (isSuccess && source) {
				const model = this.mmSourcesService.findRecord(String(source.id));
				if (model && isAlive(this)) {
					this.connectedSource = this.mmSourcesService.createModelFromRecord(model);
				}
			}
		});
	}

	@action
	openWrapperTokenTemplates() {
		const sourceType = this.args.sourceType;
		if (!sourceType) {
			return;
		}

		this.mmSourceTypesService.openWrapperTokenTemplatesModal(sourceType);
	}

	@action
	createNewDataboard() {
		this.args.onCreateNewDataboard(this.connectedSource);
	}

	@action
	openEditMetrickeyModal() {
		const spaceAccessId = this.connectedSource?.numericId;
		if (spaceAccessId) {
			this.mmMetricsService.createNewCustomPushMetric(spaceAccessId);
		}

		this.wasEditMetrickeyPopupOpened = true;
	}

	@action
	viewMetricLibrary() {
		if (this.args.onViewMetricLibrary) {
			this.args.onViewMetricLibrary(this.connectedSource);
		}
	}
	<template>
		<div class="list source-steps-list">

			{{#if this.showReadMore}}

				<div class="step">
					<img
						src="/images/onboarding/custom_flow_manual.png"
						width="109"
						height="109"
						alt
					/>
					<div>
						<span class="number">
							<CommonUiIconComponent @name="NumberCircle1" @size="lg" />
						</span>
					</div>
					<p>Learn more about our {{@sourceType.name}} integration</p>
					<a class="button size-normal" href={{this.readMoreUrl}} target="_blank">
						Read help guide
						<CommonUiIconComponent @name="External" @size="sm" />
					</a>
				</div>

			{{/if}}

			<div class="step">
				<img src="/images/onboarding/custom_flow_token.png" width="109" height="109" alt />
				<div>
					<span class="number">
						<CommonUiIconComponent @name={{this.connectStepIcon}} @size="lg" />
					</span>
				</div>
				<p>{{this.connectStepText}}</p>
				{{#if this.connectedSource}}
					<a role="button" class="button connected theme-grey-outlined size-normal">
						<CommonUiIconComponent @name="Check" @size="sm" />
						Connected
					</a>
				{{else}}
					<SubmitButtonComponent
						@text={{this.connectStepButtonText}}
						@noDelay={{true}}
						@onTrigger={{this.connectSource}}
					/>
				{{/if}}
			</div>

			{{#if this.isTokenWrapper}}

				<div class="step">
					{{#if @sourceType.isZapier}}
						<img
							src="/images/onboarding/custom_flow_zap.png"
							width="109"
							height="109"
							alt
						/>
					{{else if @sourceType.isMake}}
						<img
							src="/images/onboarding/custom-flow-make-scenario.png"
							width="auto"
							height="109"
							alt
						/>
					{{/if}}

					<div>
						<span class="number">
							<CommonUiIconComponent
								@name={{if this.showReadMore "NumberCircle3" "NumberCircle2"}}
								@size="lg"
							/>
						</span>
					</div>

					{{#if @sourceType.isZapier}}
						<p>
							Create your Zap by switching over to Zapier or use one of our
							<a role="button" {{on "click" this.openWrapperTokenTemplates}}>
								Zap templates
							</a>
						</p>
					{{else if @sourceType.isMake}}
						<p>
							Create your scenario by switching over to Make or use one of our
							<a role="button" {{on "click" this.openWrapperTokenTemplates}}>
								Make templates
							</a>
						</p>
					{{/if}}
				</div>

			{{/if}}

			{{#if this.isCustom}}

				<div class="step">
					<img
						src="/images/onboarding/custom_flow_create_metric.png"
						width="109"
						height="109"
						alt
					/>
					<div>
						<span class="number">
							<CommonUiIconComponent
								@name={{if this.showReadMore "NumberCircle3" "NumberCircle2"}}
								@size="lg"
							/>
						</span>
					</div>
					<p>Create your custom<br />metric</p>
					<SubmitButtonComponent
						@text="New custom metric"
						@noDelay={{true}}
						@isDisabled={{this.isCustomCreateMetricBtnDisabled}}
						@onTrigger={{this.openEditMetrickeyModal}}
					/>
				</div>

				<div class="step">
					<img
						src="/images/onboarding/custom_flow_push.png"
						width="109"
						height="109"
						alt
					/>
					<div>
						<span class="number">
							<CommonUiIconComponent
								@name={{if this.showReadMore "NumberCircle4" "NumberCircle3"}}
								@size="lg"
							/>
						</span>
					</div>
					<p>Start pushing your data to our Push API</p>
				</div>

			{{else}}
				<div class="step">
					<img
						src="/images/onboarding/custom_flow_designer.png"
						width="109"
						height="109"
						alt
					/>
					<div>
						<span class="number">
							<CommonUiIconComponent @name={{this.designerStepIcon}} @size="lg" />
						</span>
					</div>
					<p>Create a new dashboard from scratch</p>
					<SubmitButtonComponent
						@text="New dashboard"
						@noDelay={{true}}
						@onTrigger={{this.createNewDataboard}}
					/>
				</div>
			{{/if}}

			{{#if this.isDbWithQB}}

				<div class="step">
					<img
						src="/images/onboarding/custom_flow_add_metric.png"
						width="109"
						height="109"
						alt
					/>
					<div>
						<span class="number">
							<CommonUiIconComponent
								@name={{if this.showReadMore "NumberCircle3" "NumberCircle2"}}
								@size="lg"
							/>
						</span>
					</div>
					<p>
						In the left panel, create a new built metric under the
						{{@sourceType.name}}
						data source
					</p>
				</div>

				<div class="step">
					<img
						src="/images/onboarding/custom_flow_designer.png"
						width="109"
						height="109"
						alt
					/>
					<div>
						<span class="number">
							<CommonUiIconComponent @name={{this.designerStepIcon}} @size="lg" />
						</span>
					</div>
					<p>Add your built metric to a dashboard</p>
					<SubmitButtonComponent
						@text="Add to dashboard"
						@noDelay={{true}}
						@isDisabled={{this.disableCustomMetricButton}}
						@onTrigger={{this.createNewDataboard}}
					/>
				</div>

			{{else if this.isEmptyVisualizationFlow}}

				<div class="step">
					<img
						src="/images/onboarding/custom_flow_drag_n_drop.png"
						width="109"
						height="109"
						alt
					/>
					<div>
						<span class="number">
							<CommonUiIconComponent
								@name={{if this.showReadMore "NumberCircle4" "NumberCircle3"}}
								@size="lg"
							/>
						</span>
					</div>
					<p>Drag &amp; drop an empty chart visualization from the left panel</p>
				</div>

				<div class="step">
					<img
						src="/images/onboarding/custom_flow_select_metrics.png"
						width="109"
						height="109"
						alt
					/>
					<div>
						<span class="number">
							<CommonUiIconComponent
								@name={{if this.showReadMore "NumberCircle5" "NumberCircle4"}}
								@size="lg"
							/>
						</span>
					</div>
					<p>Select a data source and metric from the right settings panel</p>
				</div>

			{{else}}

				{{#if this.isCustom}}
					<div class="step">
						<img
							src="/images/onboarding/custom_flow_designer.png"
							width="109"
							height="109"
							alt
						/>
						<div>
							<span class="number">
								<CommonUiIconComponent
									@name={{if this.showReadMore "NumberCircle5" "NumberCircle4"}}
									@size="lg"
								/>
							</span>
						</div>
						<p>Add your custom metric to a dashboard</p>
						<SubmitButtonComponent
							@text="Go to Metric library"
							@noDelay={{true}}
							@isDisabled={{this.isViewMetricLibraryBtnDisabled}}
							@onTrigger={{this.viewMetricLibrary}}
						/>
					</div>
				{{else}}
					<div class="step">
						<img
							src="/images/onboarding/custom_flow_drag_n_drop.png"
							width="109"
							height="109"
							alt
						/>
						<div>
							<span class="number">
								<CommonUiIconComponent
									@name={{if this.showReadMore "NumberCircle5" "NumberCircle4"}}
									@size="lg"
								/>
							</span>
						</div>
						<p>
							In the left panel, select the
							{{#if this.isTokenWrapper}}
								{{@sourceType.name}}
								data source
							{{else}}
								created token
							{{/if}}
							and then drag &amp; drop the metric to the dashboard
						</p>
					</div>
				{{/if}}

			{{/if}}

		</div>
	</template>
}
